import {BufferGeometry, BufferAttribute} from 'three';

// 차량 메시의 로컬 치수. 칸 간격과 지연 마커 길이가 이 값을 기준으로 계산되므로
// (layers/traffic-layer.js) 여기서 한 번만 정하고 양쪽이 읽는다.
export const CAR_MESH_WIDTH = 0.792;
export const CAR_MESH_LENGTH = 1.584;
export const CAR_MESH_HEIGHT = 0.792;

// 셰이더는 로컬 좌표를 100 * 2^(14 - zoom) 배로 늘려 미터로 쓴다
// (mesh-set/common-vertex.glsl 의 getScale + modelScale = meterInMercatorCoordinateUnits).
// 즉 로컬 1 단위 = zoom 14 에서 100 m 이고, km 로는 로컬 / 10 이다. 경로 거리를 km 로
// 다루는 traffic-layer 가 칸 간격 계산에 이 값을 쓴다. 로컬 길이와 따로 적어두면 차량
// 크기를 바꿀 때 간격이 따라오지 않아 조용히 어긋난다 — 그래서 유도한다.
export const CAR_MESH_LENGTH_KM = CAR_MESH_LENGTH / 10;

// 칸 사이를 조금 띄워 각 칸이 제 칸으로 읽히게 한다. 지연 마커 캡슐도 이 간격만큼
// 길어야 칸을 따라 빈틈 없이 이어진다.
export const CAR_GAP_RATIO = 1.2;

// 좌표계: x = 폭, y = 길이, z = 높이(고도축).
// groupIndex 는 색 영역만 가리킨다 (CAR 은 균일 스케일이라 정점 변형에 쓰이지 않는다).
//   측면 윤곽: 0 지붕라인 / 1 상단바디 / 2 창문대 / 3 노선색 띠 / 4 하단여백
//   앞면 캡:   5 상단 / 6 유리 / 7 띠
//   뒷면 캡:   8 상단 / 9 바디 / 10 띠
const SIDE_BANDS = [[.40, 0], [.20, 1], [-.10, 2], [-.30, 3], [-1, 4]];
const FRONT_BANDS = [[.10, 5], [-.30, 6], [-1, 7]];
const BACK_BANDS = [[.10, 8], [-.30, 9], [-1, 10]];

// 높이 비율(-0.5~0.5)로 밴드를 고른다. 위에서부터 내려오며 첫 임계값 이상이면 채택.
function bandOf(ratio, bands) {
    for (const [threshold, group] of bands) {
        if (ratio >= threshold) {
            return group;
        }
    }
    return bands[bands.length - 1][1];
}

export default class extends BufferGeometry {

    constructor(width = 1, height = 1, depth = 1, radius = .26, cornerSegments = 2, capRatio = .1) {
        super();

        const me = this,
            indices = [],
            vertices = [],
            normals = [],
            uvs = [],
            groups = [],
            halfWidth = width / 2,
            halfDepth = depth / 2,
            halfLength = height / 2,
            r = Math.min(radius, halfWidth * .9, halfDepth * .9),
            cap = Math.min(capRatio * height, halfLength * .4);

        // 단면 윤곽 (x, z). 네 코너를 원호로 근사해 닫힌 고리를 만든다.
        const outline = [];
        for (const [cx, cz, a0] of [
            [halfWidth - r, halfDepth - r, 0],
            [-halfWidth + r, halfDepth - r, Math.PI / 2],
            [-halfWidth + r, -halfDepth + r, Math.PI],
            [halfWidth - r, -halfDepth + r, Math.PI * 1.5]
        ]) {
            for (let i = 0; i <= cornerSegments; i++) {
                const a = a0 + i / cornerSegments * (Math.PI / 2);
                outline.push([cx + r * Math.cos(a), cz + r * Math.sin(a)]);
            }
        }
        const n = outline.length;

        // 길이방향 링. 앞만 좁혀서 둥글게 마감하고 뒤는 각진 박스로 둔다.
        // 정점/삼각형이 줄고, 앞뒤 실루엣이 달라져 진행 방향도 더 잘 읽힌다.
        const rings = [
            [halfLength, .78],
            [halfLength - cap, 1],
            [-halfLength, 1]
        ];

        // --- 측면(윤곽 압출)
        const sideStart = vertices.length / 3;
        for (const [y, scale] of rings) {
            for (let i = 0; i < n; i++) {
                const [ox, oz] = outline[i],
                    x = ox * scale,
                    z = oz * scale,
                    len = Math.hypot(ox, oz) || 1;

                vertices.push(x, y, z);
                normals.push(ox / len, 0, oz / len);
                // u = 길이 진행도(앞->뒤, 0~0.8), v = 높이(0 바닥 ~ 1 지붕)
                uvs.push(.8 * (halfLength - y) / height, (oz / depth + .5) * .5 + .5);
                groups.push(bandOf(z / depth, SIDE_BANDS));
            }
        }
        for (let ring = 0; ring < rings.length - 1; ring++) {
            for (let i = 0; i < n; i++) {
                const a = sideStart + ring * n + i,
                    b = sideStart + ring * n + (i + 1) % n,
                    c = a + n,
                    d = b + n;

                // outline 이 x-z 평면에서 반시계이므로 이 순서가 바깥면이다
                indices.push(a, b, c, b, d, c);
            }
        }

        // --- 앞뒤 캡. 법선이 측면과 다르므로 정점을 따로 만든다.
        for (const [y, scale, bands, dir] of [
            [halfLength, .78, FRONT_BANDS, 1],
            [-halfLength, 1, BACK_BANDS, -1]
        ]) {
            const center = vertices.length / 3;

            const uBase = dir > 0 ? .8 : .9;

            vertices.push(0, y, 0);
            normals.push(0, dir, 0);
            uvs.push(uBase + .05, .75);
            groups.push(bandOf(0, bands));

            for (let i = 0; i < n; i++) {
                const [ox, oz] = outline[i];

                vertices.push(ox * scale, y, oz * scale);
                normals.push(0, dir, 0);
                uvs.push(uBase + .1 * (ox / width + .5), (oz / depth + .5) * .5 + .5);
                groups.push(bandOf(oz * scale / depth, bands));
            }
            for (let i = 0; i < n; i++) {
                const a = center + 1 + i,
                    b = center + 1 + (i + 1) % n;

                if (dir > 0) {
                    indices.push(center, b, a);
                } else {
                    indices.push(center, a, b);
                }
            }
        }

        // --- 지붕: 측면 UV 는 v 가 높이(z)라서 수평면인 지붕이 한 줄로 압축된다.
        // 그래서 별도 면으로 붙이고 텍스처 하단 절반(가로 = 길이, 세로 = 폭)에 매핑한다.
        const roofWidth = halfWidth - r,
            roofZ = halfDepth * 1.004,
            roofY = halfLength - cap,
            roofStart = vertices.length / 3;

        for (const [x, y] of [
            [-roofWidth, roofY], [-roofWidth, -roofY], [roofWidth, -roofY], [roofWidth, roofY]
        ]) {
            vertices.push(x, y, roofZ);
            normals.push(0, 0, 1);
            uvs.push(.8 * (halfLength - y) / height, (x / (2 * roofWidth) + .5) * .5);
            groups.push(0);
        }
        indices.push(
            roofStart, roofStart + 1, roofStart + 2,
            roofStart, roofStart + 2, roofStart + 3
        );

        me.setIndex(indices);
        me.setAttribute('position', new BufferAttribute(new Float32Array(vertices), 3));
        me.setAttribute('normal', new BufferAttribute(new Float32Array(normals), 3));
        // 'uv' 는 three 가 USE_UV 조건으로 선언하므로 충돌을 피해 전용 이름을 쓴다.
        // BUS 도 같은 슬롯을 쓰므로 이름은 타입 중립이다 (bus-geometry.js).
        me.setAttribute('bodyUv', new BufferAttribute(new Float32Array(uvs), 2));
        me.setAttribute('groupIndex', new BufferAttribute(new Float32Array(groups), 1));
    }

}
